import { h, Fragment } from 'preact';
import { useState, useRef, useEffect, useMemo } from 'preact/hooks';
import Panel from 'utils/panel';
import Image from 'utils/image';
import {
  formatMoney,
  formatCartLinesAjax,
} from "utils/storefront/format-resource";
import IconCloseSmall from "utils/icon/icon-close-small.svg";
import { withCartContext } from "./cart-context";
import CartItem from "./cart-item";
import CartEmpty from "./cart-empty";
import CartThreshold from "./cart-threshold";
import CartUpsells from "./cart-upsells";
import CartLoyalty from "./cart-loyalty";
import CartSamples from "./cart-samples";
import IconLoading from "utils/icon/icon-loading.svg";
import IconDiscount from "utils/icon/icon-discount.svg";
import AjaxService from "../cart/ajax-service";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay } from "swiper";
import { YouMayAlsoLike } from "./you-may-also-like";
import {
  sortLinesByPriceAndFreeSamples,
  freeSamplesAddedToCart,
} from "utils/helpers";

// TODO optimize cart + cart page
// move some parts of functionality to custom hooks to improve readability
// recheck all logic  + remove unused methods + useeffects
// A lot of duplicating logic in both components cart/cart-page, need to found solution to remove duplicating code

const Cart = (props) => {
  const { cart } = props;
  const { state, mode } = cart || {};
  const {
    subtotal,
    total_price,
    items,
    lines: stateLines,
    item_count,
    cart_level_discount_applications,
    checkoutUrl,
  } = state || {};

  const lines = useMemo(() => {
    if (mode === "storefront") {
      return stateLines || [];
    }
    return formatCartLinesAjax(items || []);
  }, [mode, stateLines, items]);

  const {
    upsell,
    searchSpringUpsellSettings,
    emptyCart,
    threshold,
    sectionPositions,
    fs_threshold,
    freeSamples,
    subscriptionAsset,
    subscriptionAssetDesktop,
    searchSpringRecommendationProducts,
    afterpayMessage,
    points,
    promoBar,
    enableYouMayAlsoLike,
    youMayAlsoLikeProducts,
    sellingPlanText,
    sellingPlanTextChoosen,
    perks,
    perksLoggedIn,
    hidePerks,
  } = props.data;

  const {
    samplesCollectionTitle: freeSamplesTag,
    samplesMaxNumber: freeSamplesMaxNumber,
    freeSamplesProductType,
  } = freeSamples;
  const closeTriggerEl = useRef(null);
  const isCartRedirect = new URLSearchParams(window.location.search).get(
    "cart"
  );
  const [panelTriggers, setPanelTriggers] = useState({
    openTriggerEl: document.querySelector("[data-header-cart-trigger]"),
    closeTriggerEl: null,
  });
  const [loading, setLoading] = useState(false);
  const [isThresholdFull, setIsThresholdFull] = useState(false);

  useEffect(() => {
    if (!closeTriggerEl.current) return;
    setPanelTriggers((value) => ({
      ...value,
      closeTriggerEl: closeTriggerEl.current,
    }));
    document.body.style.overflow = "hidden";
  }, [closeTriggerEl]);

  const hasSubscriptionProduct = useMemo(() => {
    return items ? items?.some((item) => item.selling_plan_allocation) : false;
  }, [cart, items]);

  //TODO  move to custom hook
  useEffect(() => {
    const handleKeyPress = (event) => {
      const { key } = event;
      const escKeyPressed = key && (key === "Escape" || key === "Esc");
      if (escKeyPressed) {
        document.dispatchEvent(new CustomEvent("cart:close"));
        document.body.style.overflow = "";
      }
    };
    document.addEventListener("keypress", handleKeyPress, true);
    return () => {
      document.removeEventListener("keypress", handleKeyPress);
    };
  });

  //TODO move to custom hook
  // Afterpay custom message show/hide
  useEffect(() => {
    const afterpayMin = 35;
    const afterpayMax = 1000;
    const checkElement = setInterval(() => {
      const paymentMethodSection = document.querySelector(
        "#CartDrawer square-placement"
      );

      if (paymentMethodSection) {
        clearInterval(checkElement);
        const messageAfterPay = `<div class="hidden text-center body-xs text-grey-2A mt-2.5" id="AfterpayMessage">${afterpayMessage}</div>`;
        paymentMethodSection.insertAdjacentHTML("beforebegin", messageAfterPay);

        const afterpayMessageEl = document.querySelector("#AfterpayMessage");
        const cartTotal = total_price / 100;
        const afterPayIsAvailable =
          cartTotal >= afterpayMin && cartTotal <= afterpayMax;

        if (afterPayIsAvailable) {
          paymentMethodSection.classList.remove("hidden");
          afterpayMessageEl?.classList.add("hidden");
        } else {
          paymentMethodSection.classList.add("hidden");
          afterpayMessageEl?.classList.remove("hidden");
        }
      }
    }, 50);
    return () => clearInterval(checkElement);
  }, [cart]);

  const removeCartLines = (linesToRemove) => {
    const productIdsToRemove = linesToRemove?.map((product) => product.id);
    cart.remove(productIdsToRemove);
  };

  useEffect(() => {
    document.addEventListener(`status:cart:add`, async () => await cart.get());
    if (item_count > 0) {
      const allProductsAreFreeSamples = items.every(
        (product) => product.price === 0
      );
      if (allProductsAreFreeSamples) {
        removeCartLines(items);
      }
    }
  }, [item_count]);

  const removeSamples = () => {
    const totalValue = total_price / 100;
    if (item_count > 0 && total_price && fs_threshold.active && fs_threshold.threshold > totalValue) {
      const freeSamplesProducts = items.filter(
        (product) =>
          product.price === 0 &&
          product.product_type === freeSamplesProductType &&
          !("_added_by_cartbot" in product?.properties)
      );
      if (!isThresholdFull && freeSamplesProducts.length > 0) {
        removeCartLines(freeSamplesProducts);
      }
    }
  };

  useEffect(() => {
    removeSamples();
  }, [isThresholdFull, item_count]);

  // TODO remove this logic to other file + avoid document events in react
  document.dispatchEvent(
    new CustomEvent("cart:change", {
      detail: {
        total: total_price,
      },
    })
  );

  document.addEventListener("cart:add", (event) => {
    if (event.detail.updateSellingPlans) {
      setTimeout(() => {
        setLoading(false);
      }, 4000);
    }
  });

  const freeSamplesAdded = freeSamplesAddedToCart(items, freeSamples);
  const countFreeSamples = freeSamplesAdded?.length ?? 0;
  // Determine if upsell should be shown based on conditions
  const isBelowThreshold = total_price < upsell?.threshold;
  const isAboveMinPrice = total_price >= upsell.minPrice;
  const hasValidUpsellProductId = Boolean(upsell?.productVariantId);
  const shouldShowUpsell =
    searchSpringUpsellSettings?.show && searchSpringUpsellSettings?.siteId;

  const promoBarColor =
    promoBar && promoBar?.textColor == "light"
      ? "text-white [&_p]:text-white [&_a]:text-white"
      : "text-black [&_a]:text-black [&_p]:text-black";
  // Combine conditions to determine if CartUpsells should be rendered
  const shouldRenderUpsell =
    isBelowThreshold &&
    isAboveMinPrice &&
    (hasValidUpsellProductId || shouldShowUpsell);

  const sortedLines = sortLinesByPriceAndFreeSamples(lines, freeSamplesAdded);

  const changeLoading = (state) => {
    setLoading(state);
  };

  const sortSections = () => {
    const sortedEntries = Object.entries(sectionPositions).sort(([, a], [, b]) => a - b);
    const sortedSectionsEntries = Object.fromEntries(sortedEntries);

    const sortedSections = [...Object.keys(sortedSectionsEntries)].map(section => {
      if (section === 'upsell') {
        return (
          <>
            {shouldRenderUpsell && <CartUpsells cart={cart} />}
          </>
        )
      } else if (section === 'youMayAlsoLike') {
        return (
          <>
            {enableYouMayAlsoLike && <YouMayAlsoLike youMayAlsoLikeData={youMayAlsoLikeProducts} cart={cart} />}
          </>
        )
      } else if (section === 'freeSamples') {
        return (
          <>
            {cart?.state?.total_price > 0 &&
              countFreeSamples < 2 &&
              props.data?.freeSamples?.products.length > 0 && (
                <CartSamples
                  freeSamples={freeSamples}
                  cart={cart}
                  errorMessage={fs_threshold.errorMessage}
                  isThresholdFull={isThresholdFull}
                  showErrorOnThreshold={fs_threshold.active}
                  enableFreeSamples={countFreeSamples < freeSamplesMaxNumber}
                />
            )}
          </>
        )
      }
    });
    return sortedSections;
  }

  return (
    <Panel
      triggers={panelTriggers}
      defaultOpen={isCartRedirect}
      additionalClasses={
        !loading
          ? "!max-w-[440px] h-auto group cart"
          : "!max-w-[440px] h-auto group loading cart"
      }
      showBackdrop={true}
      context="cart"
      ID="mini-cart"
      focusOutElement="[data-header-cart-trigger]"
      keyClose={true}
      overscrollNone={true}
    >
      <div className="flex flex-col h-full" id="CartDrawer">
        {promoBar && promoBar.blocks.length > 0 && (
          <div
            className="flex sm:hidden w-full py-[2px]  items-center gap-5 min-h-[36px] container mx-auto max-w-[500px] justify-center"
            style={{ backgroundColor: promoBar.backgroundColor }}
          >
            <div className="relative">
              <Swiper
                modules={[Autoplay]}
                autoplay
                centeredSlides
                loop={true}
                className="max-w-[380px]"
              >
                {promoBar.blocks.map((block, index) => (
                  <SwiperSlide className="h-auto" key={index}>
                    <div
                      className={`text-center justify-center flex h-full items-center body-xs [&_p]:body-xs [&_a]:body-xs [&_p]:max-w-[350px] [&_p]:mx-auto  md:body ${promoBarColor}`}
                      dangerouslySetInnerHTML={{ __html: block.text }}
                    ></div>
                  </SwiperSlide>
                ))}
              </Swiper>
            </div>
          </div>
        )}
        <div className="flex justify-between items-center h-[78px] px-5 border-b border-grey-D0">
          <div
            className="sub-h4 text-grey-2A"
            tabIndex={0}
            role="region"
            aria-label={`${
              window.TMW.Strings.cart.cartMessage
            }, ${item_count} ${
              item_count > 1
                ? window.TMW.Strings.cart.items
                : window.TMW.Strings.cart.item
            }`}
          >
            <span aria-hidden>
              {window.TMW.Strings.cart.title}
              {item_count > 0 && <span> ({item_count})</span>}
            </span>
          </div>

          <button
            type="button"
            tabIndex={0}
            onClick={() => {
              document.dispatchEvent(new CustomEvent("cart:close"));
              document.body.style.overflow = "";
            }}
            ariaLabel={window.TMW.Strings.cart.close}
            className="text-white hover:text-supergoop-yellow"
          >
            <IconCloseSmall />
          </button>
        </div>

        <div className="h-full overflow-y-auto flex-1">
          {!hidePerks && perks && item_count > 0 && (
            <CartLoyalty
              cart={cart}
              perks={perks}
              perksLoggedIn={perksLoggedIn}
              threshold={threshold}
              points={points}
            />
          )}
          {threshold.active && (
            <CartThreshold
              cart={cart}
              {...threshold}
              hasSubscriptionProduct={hasSubscriptionProduct}
              fsThreshold={fs_threshold}
              setIsThresholdFull={setIsThresholdFull}
            />
          )}
          {item_count === 0 && (
            <div className="flex-1 min-h-0 overflow-y-auto overscroll-none group-[.loading]:hidden w-full">
              <CartEmpty {...emptyCart} />
            </div>
          )}

          {item_count >= 0 && (
            <div className="relative">
              <div
                className="min-h-44 flex flex-col justify-center items-center text-center [&>svg]:mx-auto [&>svg]:animate-spin group-[:not(.loading)]:hidden loading-spinner"
                aria-label={window.TMW.Strings.cart.loading}
              >
                <IconLoading />

                <span
                  className="inline-block mt-2.5 body-xs text-sky-blue"
                  aria-hidden
                >
                  {threshold.loading}
                </span>
              </div>
              {sortedLines.map((line, index) => (
                <CartItem
                  key={line.id}
                  line={line}
                  index={index}
                  showLoading={changeLoading}
                  freeSamplesTag={freeSamplesTag}
                  sellingPlanText={sellingPlanText}
                  sellingPlanTextChoosen={sellingPlanTextChoosen}
                />
              ))}
            </div>
          )}

          {sortSections()}

          {searchSpringRecommendationProducts?.enable && item_count > 0 && (
            <div className="p-5 relative w-full bg-grey-F7 overflow-hidden">
              <script
                type="searchspring/personalized-recommendations"
                profile="view-cart"
              ></script>
            </div>
          )}
        </div>
        {item_count > 0 && (
          <div className="mt-auto">
            <div className="px-5 py-4">
              <Discount cart={cart} />
              <div className="flex justify-between items-center eyebrow-14 mb-2.5">
                <span>{window.TMW.Strings.cart.subtotal}</span>
                <span>
                  {cart.mode === "storefront"
                    ? formatMoney(subtotal || { amount: 0 })
                    : formatMoney(
                        total_price ? { amount: total_price } : { amount: 0 },
                        true
                      )}
                </span>{" "}
              </div>
              <a
                className="justify-center w-full button button-primary"
                href="/checkout"
                id="cart-mini-totals"
              >
                {mode === "storefront"
                  ? checkoutUrl
                  : window.TMW.Strings.cart.checkout}
              </a>
            </div>
          </div>
        )}
      </div>
    </Panel>
  );
};

export default withCartContext(Cart);

export const Discount = ({ cart }) => {
  const [discountsNew, updateDiscounts] = useState(null);

  useEffect(() => {
    const handleDiscountSetup = async () => {
      const { cart_level_discount_applications } = await AjaxService.get();
      updateDiscounts(cart_level_discount_applications);
    };
    document.addEventListener("swell:setup", handleDiscountSetup);
    return () => {
      document.removeEventListener("swell:setup", handleDiscountSetup);
    };
  }, []);

  const discounts = useMemo(() => {
    const discountList =
      cart?.state?.cart_level_discount_applications || discountsNew || [];
    return discountList.map((disc) => ({
      title: disc.title,
      total: disc.total_allocated_amount,
    }));
  }, [cart?.state?.cart_level_discount_applications, discountsNew]);

  if (discounts.length === 0) return null;

  return discounts.map(({ title, total }) => (
    <div
      key={title}
      className="flex justify-between items-center eyebrow-14 mb-3"
    >
      {title && (
        <span className="flex capitalize font-normal text-xs text-grey-2A items-center">
          <span className="mr-2">
            <IconDiscount />
          </span>
          {title}
        </span>
      )}
      {total && (
        <span className="font-normal text-xs text-grey-2A">
          (-{formatMoney({ amount: total / 100 })})
        </span>
      )}
    </div>
  ));
};
